interface UploadFileRequest {
  file: File
}

const DEFAULT_UPLOAD_URL = 'https://apiupload.aspprevweb2.com.br/files'
const DEFAULT_AUTH_TOKEN = '36e6923c0857ab9842dca293719b1d9e'
const DEFAULT_TIMEOUT_MS = 20000
const DEFAULT_MAX_RETRIES = 2

type UploadConfig = {
  url: string
  token: string
  timeoutMs: number
  maxRetries: number
}

type UploadErrorContext = {
  status?: number
  body?: string
}

function parsePositiveInt(
  value: string | undefined,
  fallback: number,
): number {
  if (!value) return fallback
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return fallback
  return Math.floor(parsed)
}

function getUploadConfig(): UploadConfig {
  const urlFromEnv = process.env.REACT_APP_UPLOAD_API_URL?.trim()
  const tokenFromEnv = process.env.REACT_APP_UPLOAD_API_TOKEN?.trim()

  const url = urlFromEnv || DEFAULT_UPLOAD_URL
  const token = tokenFromEnv || DEFAULT_AUTH_TOKEN
  const timeoutMs = parsePositiveInt(
    process.env.REACT_APP_UPLOAD_TIMEOUT_MS,
    DEFAULT_TIMEOUT_MS,
  )
  const maxRetries = parsePositiveInt(
    process.env.REACT_APP_UPLOAD_MAX_RETRIES,
    DEFAULT_MAX_RETRIES,
  )

  let normalizedUrl = ''
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') {
      throw new Error('A URL de upload deve usar HTTPS.')
    }
    normalizedUrl = parsed.toString()
  } catch {
    throw new Error('Configuração de upload inválida.')
  }

  if (!token) {
    throw new Error('Token de upload não configurado.')
  }

  return {
    url: normalizedUrl,
    token,
    timeoutMs,
    maxRetries,
  }
}

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500
}

function isRetryableError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true
  if (err instanceof TypeError) return true
  return false
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function extractErrorMessage(body: string): string {
  try {
    const parsed = JSON.parse(body) as { message?: unknown; error?: unknown }

    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim()
    }

    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error.trim()
    }
  } catch {
    // Ignore invalid JSON and fallback to the raw body.
  }

  return body.trim()
}

function extractSizeLimit(detail: string): string | null {
  const sizeMatch = detail.match(
    /(\d+(?:[.,]\d+)?)\s?(kb|mb|gb|bytes?)/i,
  )

  if (!sizeMatch) return null

  const [, value, unit] = sizeMatch
  const normalizedUnit =
    unit.toLowerCase() === 'bytes' || unit.toLowerCase() === 'byte'
      ? 'bytes'
      : unit.toUpperCase()

  return `${value.replace('.', ',')} ${normalizedUnit}`
}

function buildFriendlyUploadError(
  err: unknown,
  context: UploadErrorContext = {},
): Error {
  const rawMessage = err instanceof Error ? err.message : String(err ?? '')
  const detail = context.body ? extractErrorMessage(context.body) : rawMessage
  const normalizedDetail = detail.toLowerCase()

  if (
    context.status === 413 ||
    /file too large|payload too large|arquivo muito grande|tamanho.*exced/i.test(
      normalizedDetail,
    )
  ) {
    const sizeLimit = extractSizeLimit(detail)
    return new Error(
      sizeLimit
        ? `Arquivo muito grande. O limite para envio é ${sizeLimit}.`
        : 'Arquivo muito grande para envio. Selecione um arquivo menor e tente novamente.',
    )
  }

  if (
    (err instanceof DOMException && err.name === 'AbortError') ||
    /timeout|timed out|tempo.*esgotado|demorou/i.test(normalizedDetail)
  ) {
    return new Error(
      'O envio demorou mais do que o esperado. Tente novamente em instantes.',
    )
  }

  if (
    err instanceof TypeError ||
    /failed to fetch|networkerror|network request failed|conex[aã]o/i.test(
      normalizedDetail,
    )
  ) {
    return new Error(
      'Não foi possível enviar o arquivo agora. Verifique sua conexão e tente novamente.',
    )
  }

  if (
    context.status === 408 ||
    context.status === 429 ||
    (typeof context.status === 'number' && context.status >= 500)
  ) {
    return new Error(
      'Não foi possível enviar o arquivo agora. Tente novamente em instantes.',
    )
  }

  return new Error('Não foi possível enviar o arquivo. Tente novamente.')
}

export async function uploadFile({ file }: UploadFileRequest) {
  const { url, token, timeoutMs, maxRetries } = getUploadConfig()
  const formData = new FormData()
  formData.append('file', file)

  const totalAttempts = maxRetries + 1
  let lastError: Error | null = null

  for (let attempt = 1; attempt <= totalAttempts; attempt += 1) {
    const supportsAbortController = typeof AbortController !== 'undefined'
    const controller = supportsAbortController ? new AbortController() : null
    const timeoutId = supportsAbortController
      ? window.setTimeout(() => controller?.abort(), timeoutMs)
      : null

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: formData,
        signal: controller?.signal,
      })

      if (!response.ok) {
        const body = await response.text()
        const friendlyError = buildFriendlyUploadError(new Error(body), {
          status: response.status,
          body,
        })

        if (isRetryableStatus(response.status) && attempt < totalAttempts) {
          await wait(400 * attempt)
          continue
        }

        throw friendlyError
      }

      const data = (await response.text()).trim()
      if (!data) throw new Error('Upload concluído sem URL de retorno.')
      return data
    } catch (err: unknown) {
      if (
        attempt < totalAttempts &&
        (isRetryableError(err) || /HTTP 5\d\d|HTTP 408|HTTP 429/.test(String(err)))
      ) {
        await wait(400 * attempt)
        continue
      }

      lastError = buildFriendlyUploadError(err)
      break
    } finally {
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
  }

  throw lastError ?? new Error('Não foi possível enviar o arquivo.')
}


